/**
 * Input devices: held keys, the mouse virtual joystick and buttons, the gamepad, and edge-triggered actions.
 *
 * M2 additions (M2 plan §2.4, §6 M2-04): the tool edge actions (`toolCycle`, `toolMining`, `toolSalvage`, `dock`,
 * `powerUp`, `powerDown`), a mouse-wheel accumulator for laser power steps (`wheelSteps`, pointer-locked only,
 * drained with `drainWheel`), `scanHeld` (R or pad X held), and gamepad tap/hold timing: D-pad ↓ toggles the camera
 * on **release** when held shorter than `dockHoldSeconds` and fires `dock` once when held that long; View fires
 * `toolCycle` on release when held shorter than `viewHoldSeconds` (a longer hold belongs to M3's map).
 *
 * M6 (plan P31): `wingMenu` (Y) opens the wing radial; on the pad D-pad ↑ toggles flight assist on **release** when
 * held shorter than `wingMenuHoldSeconds` and fires `wingMenu` once when held that long.
 *
 * W2 D2 (dashboard spec §1.1–§1.2, D-617): Tab is the dashboard and `toolCycle` moves to Backquote. On the pad a View
 * tap fires `dashboard` (the View hold stays M3's map) and `toolCycle` moves to an R3 tap that a press of L3 at any
 * time during it cancels, so the L3 + R3 cruise chord never also cycles the tool. The keyboard Tab is not a polled
 * edge: the dashboard feature handles it in a capture-phase keydown listener so it can relock the pointer in the
 * gesture. The deep-link keys (N, K, U, L) open the dashboard at a section once `dashboard.json` sets `deepLinks`
 * (`deepLinkFor`; on since W3 card M4, the dashboard feature reads the flag).
 */
import type { Bindings } from '../data/schemas'
import { applyMouseDelta, createJoystick, type Joystick } from './joystick'
import type { PadLike } from './playerInput'

export type EdgeAction =
  | 'toggleFA'
  | 'toggleCamera'
  | 'pause'
  | 'devPanel'
  | 'perfOverlay'
  | 'selectPrimary1'
  | 'selectPrimary2'
  | 'cyclePrimary'
  | 'target'
  // M2 tool modes, docking and laser power.
  | 'toolCycle'
  | 'toolMining'
  | 'toolSalvage'
  | 'dock'
  | 'powerUp'
  | 'powerDown'
  // M6 (plan P31): open the wing radial (Y pressed, or D-pad ↑ held `wingMenuHoldSeconds`).
  | 'wingMenu'
  // W2 D2: toggle the dashboard (pad View tap; the keyboard Tab is the dashboard feature's capture listener).
  | 'dashboard'
  // M10 P5a7b: toggle hold fire on your crewed turret batteries (inert while weapons.json batteries.playerBatteries is off).
  | 'turretHoldFire'

type KeyEdgeAction = EdgeAction & keyof Bindings['keyboard']
type PadEdgeAction = EdgeAction & keyof Bindings['gamepad']['buttons']

/** Edge actions bound on the keyboard (weapon cycling is pad-only; keys 1 and 2 select directly). */
export const KEY_EDGE_ACTIONS: readonly KeyEdgeAction[] = [
  'toggleFA',
  'toggleCamera',
  'pause',
  'devPanel',
  'perfOverlay',
  'selectPrimary1',
  'selectPrimary2',
  'target',
  'toolCycle',
  'toolMining',
  'toolSalvage',
  'dock',
  'wingMenu',
  'turretHoldFire',
]
/**
 * Edge actions fired on the rising edge of their pad button. `toggleCamera` (tap on release, `dock` on hold),
 * `toggleFA` (tap on release, `wingMenu` on hold; M6 P31), `dashboard` (View tap on release) and `toolCycle` (R3
 * tap on release, cancelled by L3; W2 D2) are timed instead (`padEdges`).
 */
export const PAD_EDGE_ACTIONS: readonly PadEdgeAction[] = ['pause', 'cyclePrimary', 'target', 'powerUp', 'powerDown']

export interface InputDevices {
  keys: Set<string>
  joystick: Joystick
  pad: PadLike | null
  /** Edge-triggered actions since the last endFrame(). */
  pressed: Set<EdgeAction>
  padPrev: boolean[]
  /** Game-clock seconds (`padEdges`' `now`) when each pad button last went down: tap and hold timing. */
  padDownAt: number[]
  /** The hold action of a pad button already fired during its current press. */
  padHoldFired: boolean[]
  /** Mouse buttons held while pointer-locked, as a bitmask (bit n = `MouseEvent.button` n); pass to `readPlayerControl`. */
  mouseButtons: number
  /** M2: whole laser-power steps from the mouse wheel since the last `drainWheel` (+ = more power). */
  wheelSteps: number
  /** Wheel delta below one step, carried to the next event (trackpads send many small deltas). */
  wheelRemainder: number
}

export interface InputOptions {
  /** When true, mouse movement and buttons only count while the canvas holds pointer lock. */
  requirePointerLock: boolean
  onPadDisconnect: () => void
}

export function createInputDevices(): InputDevices {
  return {
    keys: new Set(),
    joystick: createJoystick(),
    pad: null,
    pressed: new Set(),
    padPrev: [],
    padDownAt: [],
    padHoldFired: [],
    mouseButtons: 0,
    wheelSteps: 0,
    wheelRemainder: 0,
  }
}

/** Records a key press: held state plus any edge action bound to `code` (ignoring auto-repeat). */
export function keyDown(dev: InputDevices, bindings: Bindings, code: string, repeat: boolean): void {
  dev.keys.add(code)
  if (repeat) return
  for (let i = 0; i < KEY_EDGE_ACTIONS.length; i++) {
    const action = KEY_EDGE_ACTIONS[i]
    if (bindings.keyboard[action].includes(code)) dev.pressed.add(action)
  }
}

/** Sets or clears mouse button `button` (0-4). A press only counts while `active` (pointer-locked). */
export function mouseButton(dev: InputDevices, button: number, down: boolean, active: boolean): void {
  if (button < 0 || button > 4) return
  if (down) {
    if (active) dev.mouseButtons |= 1 << button
  } else {
    dev.mouseButtons &= ~(1 << button)
  }
}

/** `WheelEvent.deltaMode` values (DOM constants). */
const DOM_DELTA_LINE = 1
const DOM_DELTA_PAGE = 2
/** Line-mode wheel delta per step: browsers scroll 3 lines per mouse notch. */
const LINES_PER_STEP = 3

/**
 * Accumulates a wheel event into laser-power steps (wheel up = +, unless `powerWheel.invert`). Counts only while
 * `active` (pointer-locked) and `powerWheel.enabled`; returns true when the event was used (the caller then
 * prevents the page scroll). Pixel deltas use `powerWheel.stepPixels` per step, line deltas 3 lines, page deltas 1.
 */
export function wheelInput(dev: InputDevices, bindings: Bindings, deltaY: number, deltaMode: number, active: boolean): boolean {
  const w = bindings.mouse.powerWheel
  if (!w.enabled || !active) return false
  const steps = deltaMode === DOM_DELTA_PAGE ? deltaY : deltaMode === DOM_DELTA_LINE ? deltaY / LINES_PER_STEP : deltaY / w.stepPixels
  const acc = dev.wheelRemainder + (w.invert ? steps : -steps)
  const whole = Math.trunc(acc)
  dev.wheelSteps += whole
  dev.wheelRemainder = acc - whole
  return true
}

/** The wheel's laser-power steps since the last drain (signed), then zero. */
export function drainWheel(dev: InputDevices): number {
  const n = dev.wheelSteps
  dev.wheelSteps = 0
  return n
}

/** Drops every held key and mouse button (focus or pointer lock lost). */
export function releaseAll(dev: InputDevices): void {
  dev.keys.clear()
  dev.mouseButtons = 0
  dev.wheelSteps = 0
  dev.wheelRemainder = 0
}

export function attachInput(canvas: HTMLCanvasElement, bindings: Bindings, opts: InputOptions): InputDevices {
  const dev = createInputDevices()
  const boundCodes = new Set(Object.values(bindings.keyboard).flat())
  const active = (): boolean => !opts.requirePointerLock || document.pointerLockElement === canvas

  window.addEventListener('keydown', (e) => {
    if (boundCodes.has(e.code)) e.preventDefault()
    keyDown(dev, bindings, e.code, e.repeat)
  })
  window.addEventListener('keyup', (e) => {
    dev.keys.delete(e.code)
  })
  window.addEventListener('blur', () => {
    releaseAll(dev)
  })
  window.addEventListener('mousemove', (e) => {
    if (!active()) return
    applyMouseDelta(dev.joystick, e.movementX, e.movementY, bindings.mouse.sensitivity, bindings.mouse.invertY)
  })
  // Fire buttons count only while pointer-locked, so the click that captures the pointer never fires.
  window.addEventListener('mousedown', (e) => {
    mouseButton(dev, e.button, true, active())
  })
  window.addEventListener('mouseup', (e) => {
    mouseButton(dev, e.button, false, true)
  })
  // M2 laser power: non-passive so a used wheel event never scrolls the page.
  window.addEventListener(
    'wheel',
    (e) => {
      if (wheelInput(dev, bindings, e.deltaY, e.deltaMode, active())) e.preventDefault()
    },
    { passive: false },
  )
  document.addEventListener('pointerlockchange', () => {
    if (document.pointerLockElement !== canvas) dev.mouseButtons = 0
  })
  // RMB fires torpedoes: never open the browser menu over the game.
  canvas.addEventListener('contextmenu', (e) => {
    e.preventDefault()
  })
  window.addEventListener('gamepaddisconnected', () => {
    dev.pad = null
    opts.onPadDisconnect()
  })
  return dev
}

function buttonDown(pad: PadLike, index: number): boolean {
  return pad.buttons[index]?.pressed === true
}

/** Rising edge of button `index` (against last frame's state in `padPrev`). */
function risingEdge(dev: InputDevices, pad: PadLike, index: number, action: EdgeAction): void {
  if (buttonDown(pad, index) && dev.padPrev[index] !== true) dev.pressed.add(action)
}

/**
 * A timed button: `tap` fires on release when the press lasted less than `holdSeconds`; `hold` (when not null)
 * fires once as soon as the press has lasted `holdSeconds`, and then the release fires nothing.
 */
function tapOrHold(dev: InputDevices, pad: PadLike, index: number, now: number, holdSeconds: number, tap: EdgeAction, hold: EdgeAction | null): void {
  const down = buttonDown(pad, index)
  const was = dev.padPrev[index] === true
  if (down && !was) {
    dev.padDownAt[index] = now
    dev.padHoldFired[index] = false
  } else if (down) {
    if (hold !== null && dev.padHoldFired[index] !== true && now - dev.padDownAt[index] >= holdSeconds) {
      dev.pressed.add(hold)
      dev.padHoldFired[index] = true
    }
  } else if (was && dev.padHoldFired[index] !== true && now - dev.padDownAt[index] < holdSeconds) {
    dev.pressed.add(tap)
  }
}

/**
 * A guarded tap: `tap` fires on the release of `index` when the press lasted less than `tapSeconds` and `guard` was
 * up on every polled frame of it (W2 D2: R3 alone, never the L3 + R3 cruise chord). `padHoldFired` marks a press the
 * guard (or its length) has cancelled.
 */
function guardedTap(dev: InputDevices, pad: PadLike, index: number, guard: number, now: number, tapSeconds: number, tap: EdgeAction): void {
  const down = buttonDown(pad, index)
  const was = dev.padPrev[index] === true
  if (down) {
    if (!was) {
      dev.padDownAt[index] = now
      dev.padHoldFired[index] = false
    }
    if (buttonDown(pad, guard)) dev.padHoldFired[index] = true
  } else if (was && dev.padHoldFired[index] !== true && now - dev.padDownAt[index] < tapSeconds) {
    dev.pressed.add(tap)
  }
}

/**
 * Records the pad's edge actions for this frame: rising edges of `PAD_EDGE_ACTIONS`, the D-pad ↓ camera tap (on
 * release) or dock hold, the D-pad ↑ flight-assist tap (on release) or wing-radial hold (M6 P31), the View dashboard
 * tap (on release; the hold is M3's map) and the R3 tool-cycle tap (on release, unless L3 joined it; W2 D2). `now` is
 * a monotonic clock in seconds (the frame timestamp). Every edge compares against last frame's button states, so two actions may share a button.
 * Pure apart from `dev`; allocation-free.
 */
export function padEdges(dev: InputDevices, pad: PadLike, bindings: Bindings, now: number): void {
  const g = bindings.gamepad
  const buttons = g.buttons
  for (let i = 0; i < PAD_EDGE_ACTIONS.length; i++) {
    const action = PAD_EDGE_ACTIONS[i]
    risingEdge(dev, pad, buttons[action], action)
  }
  tapOrHold(dev, pad, buttons.toggleCamera, now, g.dockHoldSeconds, 'toggleCamera', 'dock')
  tapOrHold(dev, pad, buttons.toggleFA, now, g.wingMenuHoldSeconds, 'toggleFA', 'wingMenu')
  tapOrHold(dev, pad, buttons.dashboard, now, g.viewHoldSeconds, 'dashboard', null)
  guardedTap(dev, pad, buttons.toolCycle, g.toolCycleGuardButton, now, g.toolCycleTapSeconds, 'toolCycle')
  for (let i = 0; i < pad.buttons.length; i++) dev.padPrev[i] = pad.buttons[i].pressed
}

/** Call once per frame before reading control; `now` = the frame timestamp in seconds. */
export function pollGamepad(dev: InputDevices, bindings: Bindings, now: number): void {
  const pads = navigator.getGamepads()
  let pad: Gamepad | null = null
  for (let i = 0; i < pads.length; i++) {
    const p = pads[i]
    if (p?.connected) {
      pad = p
      break
    }
  }
  dev.pad = pad
  if (!pad) return
  const g = bindings.gamepad
  padEdges(dev, pad, bindings, now)
  // Right stick in use: hand steering to the pad by centring the mouse stick.
  if (Math.abs(pad.axes[g.axes.yaw] ?? 0) > g.deadzone || Math.abs(pad.axes[g.axes.pitch] ?? 0) > g.deadzone) {
    dev.joystick.x = 0
    dev.joystick.y = 0
  }
}

/** M2: the scan key (R) or the pad scan button (X) is held. Allocation-free. */
export function scanHeld(dev: InputDevices, bindings: Bindings): boolean {
  const codes = bindings.keyboard.scan
  for (let i = 0; i < codes.length; i++) if (dev.keys.has(codes[i])) return true
  const pad = dev.pad
  return pad !== null && buttonDown(pad, bindings.gamepad.scanButton)
}

/**
 * The deep-link keys (spec §1.2): N → Intel › Wire, K → Intel › Charter, U → Fleet › Ships, L → Log › Case file.
 * The binding action names the key; the dashboard feature maps it to its tab and section.
 */
export type DeepLinkAction = 'wirePanel' | 'charterBoard' | 'fleetScreen' | 'logs'
export const DEEP_LINK_ACTIONS: readonly DeepLinkAction[] = ['wirePanel', 'charterBoard', 'fleetScreen', 'logs']

/** The deep-link action bound to `code`, or null (also whenever deep links are off). Pure. */
export function deepLinkFor(bindings: Bindings, code: string, enabled: boolean): DeepLinkAction | null {
  if (!enabled) return null
  for (let i = 0; i < DEEP_LINK_ACTIONS.length; i++) {
    const action = DEEP_LINK_ACTIONS[i]
    if (bindings.keyboard[action].includes(code)) return action
  }
  return null
}

export function drainPressed(dev: InputDevices, action: EdgeAction): boolean {
  return dev.pressed.delete(action)
}

/** Drops edge actions and wheel steps nobody consumed this frame (e.g. toggles pressed while paused). */
export function endFrame(dev: InputDevices): void {
  dev.pressed.clear()
  dev.wheelSteps = 0
}
